/**
 * <kamal-hero-scroll> — swipeable hero image carousel with dot navigation.
 * Swiping/scrolling itself is native (CSS scroll-snap); this only keeps the
 * dots in sync with the visible slide and lets a dot click jump to a slide.
 */
class KamalHeroScroll extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-kamal-hero-track]');
    this.slides = Array.from(this.querySelectorAll('[data-kamal-hero-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-kamal-hero-dot]'));

    if (!this.track || this.slides.length < 2) return;

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.slides[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      });
    });

    this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
      root: this.track,
      threshold: 0.6,
    });
    this.slides.forEach((slide) => this.observer.observe(slide));

    this.current = 0;
    this.startAutoplay();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    this.stopAutoplay();
  }

  // Auto-advance every few seconds; pauses while the user hovers, touches or
  // focuses the carousel, or when the tab is hidden. Skipped for reduced motion.
  startAutoplay() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.paused = false;
    const pause = () => (this.paused = true);
    const resume = () => (this.paused = false);
    ['mouseenter', 'touchstart', 'focusin'].forEach((e) => this.addEventListener(e, pause, { passive: true }));
    ['mouseleave', 'touchend', 'focusout'].forEach((e) => this.addEventListener(e, resume, { passive: true }));

    this.timer = setInterval(() => {
      if (this.paused || document.hidden) return;
      const next = (this.current + 1) % this.slides.length;
      this.track.scrollTo({ left: this.slides[next].offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
    }, 4000);
  }

  stopAutoplay() {
    clearInterval(this.timer);
  }

  onIntersect(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const index = this.slides.indexOf(entry.target);
      this.setActive(index);
    });
  }

  setActive(index) {
    this.current = index;
    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }
}

if (!customElements.get('kamal-hero-scroll')) {
  customElements.define('kamal-hero-scroll', KamalHeroScroll);
}
